import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MatchReplay from "../play/replay/MatchReplay";
import { fetchReplay, type LoadedReplay, type DeathReport } from "../../shared/admin-replays";
import styles from "./ReplayWatchPage.module.scss";

// Admin-only replay viewer: a stored online match (the HOST's truth) in the normal
// replay player, plus what we need to settle a disputed death:
//   - every death of the round, with who called it (the player's own client or the
//     host) and what the host says killed it. A death the host could not attribute
//     to a wall or a trail is flagged: that is what "I died in the open" looks like;
//   - the death REPORT the dying player's client sent (online/death-report.ts): its
//     clocks, what its own collision hit, and its predicted path before any snap;
//   - both drawn on the arena: the host's fatal head + cell, the client's head, the
//     client's last seconds of path.

interface Death { time: number; event: any; report: DeathReport | null; suspect: string | null }

function deathsOf(round: any, roundIndex: number, reports: DeathReport[]): Death[] {
  const events = (round.gameState.gameEvents || []).filter((e: any) => e.type === "worm_died" && e.time <= round.gameState.gameTime + 1e-6);
  const mine = reports.filter((r) => r.round === roundIndex);
  return events.map((event: any) => {
    const report = mine.find((r) => r.playerId === event.playerId && (r.report.wormId == null || r.report.wormId === event.wormId)) || null;
    let suspect: string | null = null;
    if (!event.cause && event.via === "client_report") suspect = "the client called it, the host saw nothing there";
    else if (report && report.report.calledBy === "server" && !report.report.local) suspect = "the host called it, the client saw nothing there";
    return { time: event.time, event, report, suspect };
  });
}

// The client's reported path: [t, x, y, jumping] points (online/death-report.ts).
function tailPoints(report: any): string {
  return ((report && report.tail) || []).map((p: number[]) => p[1] + "," + p[2]).join(" ");
}

function Forensics({ map, death }: { map: any; death: Death | null }) {
  if (!death) return null;
  const dbg = death.event.debug;
  const rep = death.report && death.report.report;
  const u = Math.max(map.width, map.height) / 400; // stroke unit: readable on any map size
  return (
    <svg className={styles.overlay} viewBox={`0 0 ${map.width} ${map.height}`} preserveAspectRatio="none">
      {rep && rep.tail && rep.tail.length > 0 && <polyline points={tailPoints(rep)} fill="none" stroke="#ffffff" strokeWidth={u * 0.8} strokeDasharray={`${u * 2} ${u * 2}`} opacity={0.9} />}
      {rep && rep.head && <g stroke="#ffffff" strokeWidth={u}><line x1={rep.head.x - 4 * u} y1={rep.head.y - 4 * u} x2={rep.head.x + 4 * u} y2={rep.head.y + 4 * u} /><line x1={rep.head.x - 4 * u} y1={rep.head.y + 4 * u} x2={rep.head.x + 4 * u} y2={rep.head.y - 4 * u} /></g>}
      {rep && rep.local && rep.local.cell && <rect x={rep.local.cell.col - 1.5} y={rep.local.cell.row - 1.5} width={3} height={3} fill="#ffffff" />}
      {dbg && dbg.seg && <circle cx={dbg.seg.endX} cy={dbg.seg.endY} r={Math.max(dbg.seg.size || 3, 3) + 2 * u} fill="none" stroke="#ff3b3b" strokeWidth={u} />}
      {dbg && dbg.cell && <rect x={dbg.cell.col - 1.5} y={dbg.cell.row - 1.5} width={3} height={3} fill="#ff3b3b" />}
    </svg>
  );
}

export default function ReplayWatchPage() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const key = search.get("key") || "";
  const [replay, setReplay] = useState<LoadedReplay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roundIndex, setRoundIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [open, setOpen] = useState(true);
  const controlRef = useRef<{ seekTo: (t: number) => void } | null>(null);

  useEffect(() => {
    let live = true;
    fetchReplay(key).then((res) => {
      if (!live) return;
      if ("error" in res) setError(res.error);
      else setReplay(res);
    });
    return () => { live = false; };
  }, [key]);

  const rounds = replay ? replay.match.matchState.roundsData : [];
  const deaths = useMemo(() => (replay && rounds[roundIndex] ? deathsOf(rounds[roundIndex], roundIndex, replay.deathReports) : []), [replay, roundIndex]);
  const players: any[] = replay ? replay.match.matchConfig.players : [];
  const nameOf = (id: string) => (players.find((p) => p.id === id) || {}).name || id;
  const death = selected != null ? deaths[selected] || null : null;

  if (error) return <div className={styles.message}>Could not load this replay ({error}). <button onClick={() => navigate("/admin/replays")}>Back</button></div>;
  if (!replay) return <div className={styles.message}>Loading replay…</div>;
  if (!rounds.length) return <div className={styles.message}>This replay has no rounds. <button onClick={() => navigate("/admin/replays")}>Back</button></div>;

  const reportsIn = (i: number) => replay!.deathReports.some((r) => r.round === i);

  function pick(i: number) {
    setSelected(i);
    // A beat before the crash, held: the scrubber takes it from there.
    if (controlRef.current) controlRef.current.seekTo(Math.max(0, deaths[i].time - 1.5));
  }

  return (
    <>
      <MatchReplay
        match={replay.match}
        round={roundIndex /* owned here: the panel's round tabs drive the player too */}
        onExit={() => navigate("/admin/replays")}
        onRoundChange={(i) => { setRoundIndex(i); setSelected(null); }}
        controlRef={controlRef}
        roundMark={reportsIn}
        arenaOverlay={<Forensics map={rounds[roundIndex].gameState.map} death={death} />}
      />
      <div className={styles.panel + (open ? "" : " " + styles.closed)}>
        <div className={styles.head}>
          <button className={styles.toggle} onClick={() => setOpen(!open)}>{open ? "Hide" : "Deaths"}</button>
          {open && <span className={styles.roundsLabel}>Round</span>}
          {open && (
            <div className={styles.rounds}>
              {rounds.map((_: any, i: number) => (
                <button key={i} className={i === roundIndex ? styles.on : undefined} onClick={() => { setRoundIndex(i); setSelected(null); }} title={"Round " + (i + 1)}>
                  {i + 1}{reportsIn(i) ? "•" : ""}
                </button>
              ))}
            </div>
          )}
        </div>
        {open && (
          <>
            <ul className={styles.deaths}>
              {deaths.map((d, i) => (
                <li key={i} className={[i === selected ? styles.on : "", d.suspect ? styles.suspect : ""].join(" ")} onClick={() => pick(i)}>
                  <span className={styles.t}>{d.time.toFixed(1)}s</span>
                  <span className={styles.who}>{nameOf(d.event.playerId)}</span>
                  <span className={styles.cause}>
                    {d.event.cause === "trail" ? "hit " + nameOf(d.event.killerPlayerId) : d.event.cause === "wall" ? "wall" : "no cause"}
                    {" · "}{d.event.via === "client_report" ? "client" : "host"}
                    {d.report ? " · report" : ""}
                  </span>
                </li>
              ))}
              {deaths.length === 0 && <li className={styles.none}>No deaths in this round.</li>}
            </ul>
            {death && (
              <div className={styles.detail}>
                {death.suspect && <p className={styles.flag}>Suspect: {death.suspect}.</p>}
                <p className={styles.legend}><b className={styles.red}>Red</b> = the host's fatal head and cell. <b>White</b> = the client's head (×), hit cell and last path.</p>
                <pre>{JSON.stringify({ event: death.event, report: death.report ? { ...death.report.report, tail: undefined } : null }, null, 1)}</pre>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
